/**
 * Unified color scheme for pipeline steps and LLM call purposes.
 * All colors are defined as Tailwind theme colors.
 */

/**
 * Pipeline step colors for timing progress bar and indicators.
 */
export const PIPELINE_STEP_COLORS = {
  download: 'pipeline-download',
  transcribe: 'pipeline-transcribe',
  extract: 'pipeline-extract',
  enrich: 'pipeline-enrich',
  save: 'pipeline-save',
} as const;

/**
 * Pipeline step background color classes for use in templates.
 */
export const PIPELINE_STEP_BG_CLASSES = {
  download: 'bg-pipeline-download',
  transcribe: 'bg-pipeline-transcribe',
  extract: 'bg-pipeline-extract',
  enrich: 'bg-pipeline-enrich',
  save: 'bg-pipeline-save',
} as const;

export type PipelineStep = keyof typeof PIPELINE_STEP_COLORS;

/**
 * LLM background color classes for badge bodies.
 * Each LLM purpose uses a darker/richer variant of its pipeline step color.
 */
export const LLM_BG_COLOR_CLASSES: Record<string, string> = {
  transcription: 'bg-llm-transcription',
  summarization: 'bg-llm-summarization',
  entity_extraction: 'bg-llm-entity-extraction',
  recipe_extraction: 'bg-llm-recipe-extraction',
  context_cache: 'bg-llm-context-cache',
  classification: 'bg-llm-classification',
};

/**
 * LLM text color classes for readability.
 * Dark backgrounds use white text, light backgrounds use dark text.
 */
export const LLM_TEXT_COLOR_CLASSES: Record<string, string> = {
  transcription: 'text-llm-transcription-text',
  summarization: 'text-llm-summarization-text',
  entity_extraction: 'text-llm-entity-extraction-text',
  recipe_extraction: 'text-llm-recipe-extraction-text',
  context_cache: 'text-llm-context-cache-text',
  classification: 'text-llm-classification-text',
};

/**
 * Map each LLM purpose to its pipeline step.
 * Used to determine the strip color.
 */
export const PURPOSE_TO_PIPELINE_STEP: Record<
  string,
  keyof typeof PIPELINE_STEP_COLORS
> = {
  transcription: 'transcribe',
  summarization: 'transcribe',
  entity_extraction: 'extract',
  recipe_extraction: 'extract',
  context_cache: 'download',
  classification: 'save',
};

/**
 * Get the badge background class for an LLM purpose.
 * Returns Tailwind class for the LLM-specific background color.
 */
export function purposeBadgeBgClass(purpose: string | null): string {
  if (!purpose) return '';
  return LLM_BG_COLOR_CLASSES[purpose] || '';
}

/**
 * Get the badge strip class for an LLM purpose.
 * Returns Tailwind class with the pipeline step color for the left strip.
 */
export function purposeBadgeStripClass(purpose: string | null): string {
  if (!purpose) return '';
  const pipelineStep = PURPOSE_TO_PIPELINE_STEP[purpose];
  return pipelineStep ? `bg-${PIPELINE_STEP_COLORS[pipelineStep]}` : '';
}

/**
 * Get the badge text color class for an LLM purpose.
 * Returns Tailwind class for the appropriate text color.
 */
export function purposeBadgeTextColorClass(purpose: string | null): string {
  if (!purpose) return '';
  return LLM_TEXT_COLOR_CLASSES[purpose] || '';
}
